import { Pipe, PipeTransform } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UserReducedDto } from 'projects/cupcake/src/app/xatena-api';

interface ISelectOption {
  label: string;
  disabled?: boolean;
  value?: any;
  group?: ISelectOption[];
  data?: UserReducedDto;
}

interface ITransformOption {
  labelProp: (option: any) => string;
  valueProp: (option: any) => any;
  disabledProp: (option: any) => boolean;
  groupProp: (option: any) => string;
  dataProp: (option: UserReducedDto) => UserReducedDto;
}

@Pipe({
  name: 'formlyUserSelectOptions',
  standalone: true,
})
export class FormlyUserSelectOptionsPipe implements PipeTransform {
  transform(
    options: any,
    field?: FormlyFieldConfig
  ): Observable<ISelectOption[]> {
    if (!(options instanceof Observable)) {
      options = observableOf(options);
    }

    return (options as Observable<any>).pipe(
      map(value => this.transformOptions(value, field))
    );
  }

  private transformOptions(
    options: any[],
    field?: FormlyFieldConfig
  ): ISelectOption[] {
    const to = this.transformSelectProps(field);

    const opts: ISelectOption[] = [];
    const groups: Record<string, number> = {};

    options.forEach(option => {
      const o = this.transformOption(option, to);
      if (o.group) {
        const id = groups[o.label];
        if (id === undefined) {
          groups[o.label] = opts.push(o) - 1;
        } else {
          o.group.forEach(o => {
            const tmp = opts[id];
            if (tmp.group) {
              tmp?.group.push(o);
            }
          });
        }
      } else {
        opts.push(o);
      }
    });

    if (field?.templateOptions) {
      field.templateOptions._flatOptions = !Object.keys(groups).length;
    }

    return opts;
  }

  private transformOption(option: any, props: ITransformOption): ISelectOption {
    const group = props.groupProp(option);
    if (Array.isArray(group)) {
      return {
        label: props.labelProp(option),
        group: group.map(opt => this.transformOption(opt, props)),
      };
    }

    option = {
      label: props.labelProp(option),
      value: props.valueProp(option),
      disabled: !!props.disabledProp(option),
      data: props.dataProp(option),
    };

    if (group) {
      return { label: group, group: [option] };
    }

    return option;
  }

  private transformSelectProps(field?: FormlyFieldConfig): ITransformOption {
    const props = field?.templateOptions || field?.templateOptions || {};
    const selectPropFn = (prop: any) =>
      typeof prop === 'function' ? prop : (o: any) => o[prop];

    return {
      groupProp: selectPropFn(props.groupProp || 'group'),
      labelProp: selectPropFn(props.labelProp || 'label'),
      valueProp: selectPropFn(props.valueProp || 'value'),
      disabledProp: selectPropFn(props.disabledProp || 'disabled'),
      dataProp: selectPropFn(props.dataProp || 'data'),
    };
  }
}
